import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Author } from 'src/types';

interface SEOProps {
  title: string;
  description: string;
  imageUrl?: string;
  schemaData?: BlogPostSchemaData | AuthorSchemaData;
  breadCrumbs?: BreadCrumbsData[];
}

interface BlogPostSchemaData {
  headline: string;
  datePublished: Date;
  author?: Author;
  description: string;
  imageUrl: string;
}

interface AuthorSchemaData {
  name: string;
  description: string;
  imageUrl: string;
}

interface Query {
  site: {
    siteMetadata: {
      defaultTitle: string;
      titleTemplate: string;
      defaultDescription: string;
      siteUrl: string;
      name: string;
    };
  };
}

interface BreadCrumbsData {
  title: string;
  path: string;
}

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl
        name
      }
    }
  }
`;

const generateUrl = (siteUrl: string, pathname: string) => {
  const url = new URL(siteUrl);
  let newUrl = url.origin;
  if (pathname !== '/') {
    newUrl = newUrl + pathname;
  }
  if (newUrl.charAt(newUrl.length - 1) !== '/') {
    newUrl = newUrl + '/';
  }
  return newUrl;
};

const generateBlogPostSchemaMarkup = (
  schemaData: BlogPostSchemaData,
  siteUrl: string,
  url: string
) => {
  let author = null;
  if (schemaData?.author) {
    const authorUrl = generateUrl(siteUrl, `/learn/author/${schemaData?.author?.slug?.current}/`);
    author = {
      '@type': 'Person',
      name: schemaData?.author?.name,
      url: authorUrl
    };
  }
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: schemaData.headline,
    datePublished: schemaData.datePublished,
    author: author,
    publisher: {
      '@type': 'Organization',
      '@id': 'https://www.ownup.com/',
      name: 'OwnUp',
      url: 'https://www.ownup.com/'
    },
    description: schemaData.description,
    image: {
      '@type': 'ImageObject',
      url: schemaData.imageUrl
    },
    url: url
  });
};

const generateAuthorSchemaMarkup = (schemaData: AuthorSchemaData, url: string) => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: schemaData.name,
    description: schemaData.description,
    url: url,
    image: {
      '@type': 'ImageObject',
      url: schemaData.imageUrl
    }
  });
};

const generateBreadcrumbListSchemaMarkup = (breadcrumbsData: BreadCrumbsData[], url: string) => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': url,
    itemListElement: breadcrumbsData.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.title,
      item: breadcrumb.path
    }))
  });
};

export const SEO = ({ title, description, imageUrl, schemaData, breadCrumbs }: SEOProps) => {
  const { pathname } = useLocation();
  const { site }: Query = useStaticQuery(query);

  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    name: siteName
  } = site.siteMetadata;

  const url = generateUrl(siteUrl, pathname);

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    imageUrl,
    url
  };

  const meta = [
    { name: 'description', content: seo.description },
    { name: 'title', content: `${seo.title} | Own Up Resources` },
    { property: 'og:title', content: seo.title },
    { property: 'og:description', content: seo.description },
    { property: 'og:site_name', content: siteName },
    { property: 'og:type', content: pathname ? 'article' : 'website' },
    { property: 'og:url', content: seo.url },
    { property: 'og:image', content: seo.imageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: `${seo.title} | Own Up Resources` },
    { name: 'twitter:description', content: seo.description },
    { name: 'twitter:image', content: seo.imageUrl },
    { name: 'referrer', content: 'no-referrer' }
  ];

  let schemaMarkup = null;
  let breadCrumbsSchemaMarkup = null;

  if (schemaData) {
    if ('headline' in schemaData) {
      // Blog post schema
      const blogPostSchema = schemaData as BlogPostSchemaData;
      schemaMarkup = generateBlogPostSchemaMarkup(blogPostSchema, siteUrl, url);
    } else if ('name' in schemaData) {
      // Author schema
      const authorSchema = schemaData as AuthorSchemaData;
      schemaMarkup = generateAuthorSchemaMarkup(authorSchema, url);
    }
  }

  if (breadCrumbs) {
    breadCrumbsSchemaMarkup = generateBreadcrumbListSchemaMarkup(breadCrumbs, url);
  }

  return (
    <Helmet title={seo.title} titleTemplate={titleTemplate} meta={meta}>
      <html lang="en" />
      <link
        rel="canonical"
        key={seo.url}
        href={seo.url}
        data-baseprotocol="https:"
        data-basehost="www.ownup.com"
      />
      {schemaMarkup && <script type="application/ld+json">{schemaMarkup}</script>}
      {breadCrumbsSchemaMarkup && (
        <script type="application/ld+json">{breadCrumbsSchemaMarkup}</script>
      )}
    </Helmet>
  );
};
